import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import { ExternalLink, LayoutGrid } from 'lucide-react';
import type { ServiceEntry } from '@/lib/api';
import type { ServiceGroup } from '@shared/services';

const REFETCH = 5_000;

const GROUP_ORDER: ServiceGroup[] = [
  'network',
  'devops',
  'media',
  'arr',
  'minecraft',
  'self-host',
];

const GROUP_LABELS: Record<ServiceGroup, string> = {
  network: 'Network / edge',
  devops: 'DevOps',
  media: 'Media',
  arr: '*arr stack',
  minecraft: 'Minecraft',
  'self-host': 'Self-host',
};

export function LauncherRoute() {
  const q = useQuery({
    queryKey: ['services'],
    queryFn: endpoints.services.list,
    refetchInterval: REFETCH,
  });

  const services = q.data?.services ?? [];
  const grouped = useMemo(() => {
    const map = new Map<ServiceGroup, ServiceEntry[]>();
    for (const svc of services) {
      const list = map.get(svc.group) ?? [];
      list.push(svc);
      map.set(svc.group, list);
    }
    for (const [, list] of map) list.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return map;
  }, [services]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Launcher</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Quick links to every service in the homelab. Click an external link icon to open the web UI.
        </p>
      </div>

      {GROUP_ORDER.filter((g) => grouped.has(g)).map((group) => {
        const list = grouped.get(group) ?? [];
        return (
          <Card key={group}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutGrid className="h-3.5 w-3.5" />
                {GROUP_LABELS[group]}
              </CardTitle>
              <span className="text-xs text-muted-foreground">{list.length} service(s)</span>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {list.map((s) => (
                  <ServiceTile key={s.id} svc={s} />
                ))}
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}

function ServiceTile({ svc }: { svc: ServiceEntry }) {
  const inner = (
    <div className="rounded-md border border-border bg-card/60 p-3 flex items-center gap-3 hover:border-primary/40 hover:bg-accent/30 transition-colors">
      <StatusIcon status={svc.health.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{svc.displayName}</span>
        </div>
        <div className="text-xs text-muted-foreground font-mono">{svc.ip}</div>
      </div>
      <StatusBadge status={svc.health.status} showIcon={false} />
      {svc.webUrl && <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />}
    </div>
  );
  if (svc.webUrl) {
    return (
      <a href={svc.webUrl} target="_blank" rel="noreferrer noopener" className="block">
        {inner}
      </a>
    );
  }
  return inner;
}
